import {
  SHButton,
  SHDivider,
  SHFormControlLabel,
  SHPopper,
  SHRadio,
  SHRadioGroup,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { FilterSVG } from "@components/svgs";
import { EditSVG } from "@components/svgs/edit";
import { useClickAwayRefs } from "@hooks/useClickAwayRefs";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { AnalysisVirtualizedStepProps } from "@models/platform-analysis/entities/analysis";
import { FeatureAnalysisCollapseStateFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisCollapseLevelFilter";
import { FeatureAnalysisEssentialFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisEssentialFilter";
import { FeatureAnalysisAvailabilityFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisFilter";
import { useTheme } from "@mui/material";
import {
  FeatureAnalysisAvailabilityFilterOptions,
  FeatureAnalysisCollapseLevelFilterOptions,
  FeatureAnalysisEssentialFeatureFilterOptions,
} from "@pages/platform-analysis-v2/_id/constants";
import { hexToRGBA } from "@utils/color";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useUnmount } from "react-use";

export const AnalysisTableSettings = ({
  virtualizedProps,
}: {
  virtualizedProps: AnalysisVirtualizedStepProps;
}) => {
  const { palette } = useTheme();
  const { updateFeatureAnalysisRowFilters } = usePlatformAnalysis();

  const storedCollapseStateChangedManually = useSelector(
    virtualizedProps.getCollapseLevelChangedManuallyFunc,
  );

  // #region Refs & States
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [featureFilter, setFeatureFilter] =
    useState<FeatureAnalysisAvailabilityFilterLevel>(
      FeatureAnalysisAvailabilityFilterLevel.All,
    );
  const [essentialFeatureFilter, setEssentialFeatureFilter] =
    useState<FeatureAnalysisEssentialFilterLevel>(
      FeatureAnalysisEssentialFilterLevel.All,
    );
  const [collapseLevelFilter, setCollapseLevelFilter] = useState<
    FeatureAnalysisCollapseStateFilterLevel | undefined
  >(FeatureAnalysisCollapseStateFilterLevel.FeatureLevel);
  // #endregion Refs & States

  // #region Handlers
  const handleOnOpenPopup = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  const updateFilters = ({
    collapseLevel = collapseLevelFilter,
    essentialLevel = essentialFeatureFilter,
    availabilityLevel = featureFilter,
    resetCollapseStateChangedManually = false,
  }: {
    collapseLevel?: FeatureAnalysisCollapseStateFilterLevel;
    essentialLevel?: FeatureAnalysisEssentialFilterLevel;
    availabilityLevel?: FeatureAnalysisAvailabilityFilterLevel;
    resetCollapseStateChangedManually?: boolean;
  }) => {
    updateFeatureAnalysisRowFilters({
      collapseLevel,
      essentialLevel,
      availabilityLevel,
      resetCollapseStateChangedManually,
    });
  };

  const handleOnExpandCollapseFilterChange = (
    value: FeatureAnalysisCollapseStateFilterLevel,
  ) => {
    setCollapseLevelFilter(value);
    updateFilters({
      collapseLevel: value,
      resetCollapseStateChangedManually: true,
    });
  };

  const handleOnEssentialFeatureFilterChange = (
    value: FeatureAnalysisEssentialFilterLevel,
  ) => {
    setEssentialFeatureFilter(value);
    updateFilters({ essentialLevel: value });
  };

  const handleOnAvailabilityFilterChange = (
    value: FeatureAnalysisAvailabilityFilterLevel,
  ) => {
    setFeatureFilter(value);
    updateFilters({ availabilityLevel: value });
  };
  // #endregion Handlers

  // #region Effects & Variables
  useClickAwayRefs([wrapperRef, buttonRef], () => {
    setOpen(false);
  });

  useUnmount(() => {
    updateFilters({
      collapseLevel: FeatureAnalysisCollapseStateFilterLevel.FeatureLevel,
      essentialLevel: FeatureAnalysisEssentialFilterLevel.All,
      availabilityLevel: FeatureAnalysisAvailabilityFilterLevel.All,
      resetCollapseStateChangedManually: true,
    });
  });

  useEffect(() => {
    if (!storedCollapseStateChangedManually) return;
    setCollapseLevelFilter(undefined);
  }, [storedCollapseStateChangedManually]);
  // #endregion Effects & Variables

  return (
    <>
      <SHButton
        ref={buttonRef}
        onClick={handleOnOpenPopup}
        color="primary"
        variant="text"
        size="extraMedium"
        startIcon={<EditSVG color={palette.primary.main} />}
        sx={{ width: "120px" }}
      >
        Settings
      </SHButton>

      <SHPopper
        open={open}
        anchorEl={anchorEl}
        placement="left-start"
        popperOptions={{
          modifiers: [
            { name: "flip", enabled: false },
            { name: "offset", options: { offset: [0, -120] } },
          ],
        }}
        sx={{ zIndex: 10 }}
      >
        <SHStack
          maxWidth={300}
          ref={wrapperRef}
          boxSizing={"border-box"}
          boxShadow={"none"}
          borderColor={palette.divider}
          border={`1px solid ${palette.divider}`}
          borderRadius={0}
          bgcolor={hexToRGBA(palette.common.white, 0.95)}
          paddingX={2}
          paddingY={1}
          gap={1}
          sx={{
            filter: "drop-shadow(1px 1px 2px rgba(65, 73, 89, 0.25))",
          }}
        >
          {/* Feature filters */}
          <SHStack>
            <SHStack flexDirection="row" alignItems="center" gap={0.5}>
              <FilterSVG />
              <SHTypography variant="body1" marginLeft={1}>
                Feature filters
              </SHTypography>
            </SHStack>

            <SHRadioGroup value={featureFilter} sx={{ gap: 0 }}>
              {FeatureAnalysisAvailabilityFilterOptions.map((option) => (
                <SHFormControlLabel
                  size="small"
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  control={
                    <SHRadio
                      size="small"
                      sx={{ marginLeft: 0.5 }}
                      onClick={() =>
                        handleOnAvailabilityFilterChange(option.value)
                      }
                    />
                  }
                />
              ))}
            </SHRadioGroup>
          </SHStack>

          <SHDivider />

          {/* Essential features */}
          <SHStack>
            <SHStack flexDirection="row" alignItems="center" gap={0.5}>
              <FilterSVG />
              <SHTypography variant="body1" marginLeft={1}>
                Essential features
              </SHTypography>
            </SHStack>

            <SHRadioGroup value={essentialFeatureFilter} sx={{ gap: 0 }}>
              {FeatureAnalysisEssentialFeatureFilterOptions.map((option) => (
                <SHFormControlLabel
                  size="small"
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  control={
                    <SHRadio
                      size="small"
                      sx={{ marginLeft: 0.5 }}
                      onClick={() =>
                        handleOnEssentialFeatureFilterChange(option.value)
                      }
                    />
                  }
                />
              ))}
            </SHRadioGroup>
          </SHStack>

          <SHDivider />

          {/* Expand/collapse rows */}
          <SHStack>
            <SHStack flexDirection="row" alignItems="center" gap={0.5}>
              <FilterSVG />
              <SHTypography variant="body1" marginLeft={1}>
                Expand/collapse rows
              </SHTypography>
            </SHStack>

            <SHRadioGroup value={collapseLevelFilter} sx={{ gap: 0 }}>
              {FeatureAnalysisCollapseLevelFilterOptions.map((option) => (
                <SHFormControlLabel
                  size="small"
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  control={<SHRadio size="small" sx={{ marginLeft: 0.5 }} />}
                  onClick={() =>
                    handleOnExpandCollapseFilterChange(option.value)
                  }
                />
              ))}
            </SHRadioGroup>
          </SHStack>
        </SHStack>
      </SHPopper>
    </>
  );
};
